import { Component, inject } from '@angular/core';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { CountryService } from 'src/app/trip-calculator/services/country.service';
import { TripCreationModalService } from 'src/app/trip-calculator/services/trip-creation-modal.service';
import { TripService } from 'src/app/trip-calculator/services/trip.service';
import { BannerComponent } from '../../banner/banner.component';
import { PrestationModalService } from 'src/app/trip-calculator/services/prestation-modal.service';
import { TripValidationBadgeComponent } from '../../trip-validation-badge/trip-validation-badge.component';
import { BrochureDownloadService } from 'src/app/shared/services/brochure-download.service';
import { PackLimitationsService } from 'src/app/shared/services/pack-limitations.service';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'app-trip-header',
  imports: [
    TranslocoPipe,
    ButtonModule,
    TooltipModule,
    BannerComponent,
    TripValidationBadgeComponent,
    DatePipe,
  ],
  templateUrl: './trip-header.component.html',
})
export class TripHeaderComponent {
  tripService = inject(TripService);
  tripCreationModalService = inject(TripCreationModalService);
  countryService = inject(CountryService);
  prestationModalService = inject(PrestationModalService);
  brochureDownloadService = inject(BrochureDownloadService);
  translocoService = inject(TranslocoService);
  packLimitationService = inject(PackLimitationsService);

  get isCountryMissing(): boolean {
    return !this.tripService.trip()?.countryCode;
  }

  get countryName() {
    const country = this.tripService.trip()?.tripCountry?.[0]?.countries;
    return country
      ? this.countryService.getCountryLabelByLang(country)
      : this.tripService.trip()?.countryCode;
  }

  get clientName(): string {
    const clientList = this.tripService.trip()?.companyClients || [];
    return clientList.map((client) => client.name).join(', ');
  }
  get hasClient() {
    if (!this.packLimitationService.canFilterClient()) return false;
    const clients = this.tripService.trip()?.companyClients ?? [];
    return clients.length > 0;
  }

  get hasBrochures() {
    return this.brochureDownloadService.hasBrochures(this.tripService.trip());
  }

  get brochures() {
    return this.brochureDownloadService.getBrochures(this.tripService.trip());
  }

  getBrochureTooltip(filename: string): string {
    const baseTooltip = this.translocoService.translate(
      'dashboard.brochureTooltip',
    );
    return `${filename}\n\n${baseTooltip}`;
  }

  /**
   * Scroll to and open the modal for the first invalid item
   * This is triggered when user clicks on the validation badge
   */
  scrollToFirstInvalidItem = (): void => {
    const report = this.tripService.validationReport();
    if (!report || report.issues.length === 0) return;

    // Find first error, or first warning if no errors
    const firstError = report.issues.find(
      (issue) => issue.severity === 'error',
    );
    const firstIssue = firstError || report.issues[0];

    // Navigate to the appropriate modal based on scope
    switch (firstIssue.scope) {
      case 'transport':
        if (firstIssue.index !== undefined) {
          this.prestationModalService.editTransport(firstIssue.index);
        } else {
          this.prestationModalService.addTransport();
        }
        break;
      case 'accommodation':
        if (firstIssue.index !== undefined) {
          this.prestationModalService.editAccommodation(firstIssue.index);
        } else {
          this.prestationModalService.addAccommodation();
        }
        break;
      case 'activity':
        if (firstIssue.index !== undefined) {
          this.prestationModalService.editActivity(firstIssue.index);
        } else {
          this.prestationModalService.addActivity();
        }
        break;
      case 'meal':
        if (firstIssue.index !== undefined) {
          this.prestationModalService.editMeal(firstIssue.index);
        } else {
          this.prestationModalService.addMeal();
        }
        break;
      case 'trip':
        // Only field-bound trip issues are fixable in the trip settings modal;
        // the others (no accommodation, no meal) have no single place to go.
        if (firstIssue.fieldPath) {
          this.tripCreationModalService.updateTrip();
        }
        break;
    }
  };

  triggerValidation(): void {
    const trip = this.tripService.trip();
    if (!trip?.id) return;

    // Use centralized validation trigger
    this.tripService.triggerValidation(trip.id);
  }
}
